import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { differenceBy } from 'lodash';
import { HTTPService, HelperService } from 'app/services';
import { systemSpecificTags } from 'app/constants';
import { ConfirmDismissDialogComponent } from '../confirm-dismiss-dialog/confirm-dismiss-dialog.component';

@Component({
  selector: 'app-tags-list',
  templateUrl: './tags-list.component.html',
  styleUrls: ['./tags-list.component.scss'],
})
export class TagsListComponent implements OnInit {
  @Input() set tags(tags: any[]) {
    this._tags = [...tags.filter(({ name }) => systemSpecificTags.indexOf(name) === -1)];
  }
  @Input() type: string = '';
  @Input() id: string = '';
  @Input() buttonId: string = '';
  @Input() tagIdField: string = '_id';
  @Input() tagLabelField: string = 'name';
  @Input() tagColorField: string = 'color';
  @Input() hasCreateOption: boolean = false;
  @Input() isDeleteable: boolean = false;
  @Input() disabled: boolean = false;
  @Input() selectedTags: any[] = [];
  @Input() extraTags: any[] = [];
  @Input() hideSelectedTags: boolean = true;
  @Input() addTagTitle: string = '';
  @Input() fontSize: number = 12;
  @Input() visibleItem: number = 0;
  @Input() maxLimit: number = 3;
  @Input() inputBoxWidth: string = '235px';
  @Input() itemWidth: string = '235px';
  @Input() scrollHeight: string = '192px';
  @Input() stopPropagation: boolean = false;
  @Input() stopPropagationOnDelete: boolean = false;
  @Input() isSetPositionDynamically: boolean = false;
  @Input() hPosition: string = '';
  @Input() vPosition: string = '';
  @Input() extraTagTruncate: boolean = true;
  @Input() tagHeight: number = 0;

  @Output() appSelect = new EventEmitter<any>();
  @Output() appShowSuggestion = new EventEmitter<any>();
  @Output() appDelete = new EventEmitter<any>();
  @Output() appCreateTag = new EventEmitter<any>();
  @Output() appUpdateTag = new EventEmitter<any>();
  @Output() appUpdateTagList = new EventEmitter<any>();
  @Output() appClick = new EventEmitter<any>();
  @Output() appTagDeleted = new EventEmitter<any>();

  _tags: any[] = [];
  tagsResult: any[] = [];
  visibleTags: any[] = [];
  isCreateMode: boolean = false;

  constructor(private httpService: HTTPService, private helperService: HelperService, private diaogService: DialogService) {}

  ngOnInit() {
    let tagLength = 0;
    if (this.selectedTags) {
      for (let i = 0; i < this.selectedTags.length; i++) {
        if(Object.keys(this.selectedTags[i] || {})?.length) {
          tagLength += this.selectedTags[i][this.tagLabelField]?.length;
          if (tagLength > 25 || this.visibleTags.length === 3) {
            break;
          }
          this.visibleTags.push(this.selectedTags[i]);
        } else {
          this.selectedTags.splice(i,1);
        }
        
      }
    }
  }

  /** Toggle Create Tag Field */
  toggleCreateTag(state: boolean, event?: Event) {
    if (this.stopPropagation) {
      event.stopPropagation();
    }

    if (this.disabled) {
      return;
    }

    this.isCreateMode = state;
  }

  /** Show tags suggestions */
  showTagsSuggestion() {
    this.tagsResult = differenceBy(this._tags, this.selectedTags, this.tagLabelField);
    this.appShowSuggestion.emit();
  }

  /** On selecting suggestion */
  onSelect(tag: any) {
    const trimmedName = tag.name.trim();

    if (!Array.isArray(this.selectedTags)) {
      this.selectedTags = [];
    }
  
    const isDuplicate = this.selectedTags.some(selectedTag => selectedTag.name.toLowerCase() === trimmedName.toLowerCase());
  
    if (isDuplicate) {
      this.helperService.showToaster('error', 'Record already exists.');
      return;
    }
  
    this.isCreateMode = false;
    this.appSelect.emit(tag);
  }

  /** Searching for tags */
  onSearch(value) {
    const query = value.toLowerCase();
    this.tagsResult = this._tags.filter(({ name }) => name.toLowerCase().includes(query));
  }

  /** On tag creation */
  onCreateTag({ name, color, isForFurtureUse }) {
    const trimmedName = name.trim();

    if (!Array.isArray(this._tags)) {
      this._tags = [];
    }
    
    const isDuplicate = this._tags.some(tag => tag.name.toLowerCase() === trimmedName.toLowerCase());
  
    if (isDuplicate) {
      this.helperService.showToaster('error', 'Record already exists.');
      return;
    }
  
    const tag = {
      name: trimmedName,
      color: color,
      type: this.type,
      isForFurtureUse,
    };
    this.isCreateMode = false;
    if (isForFurtureUse) {
      this.httpService.createTag(tag).then(
        (data) => {
          this.onSelect(data);
          this.appCreateTag.emit(data);
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      );
    } else {
      this.onSelect(tag);
      this.appCreateTag.emit(tag);
    }
  }

  onUpdateItem({ name, color, _id }) {
    const tag = {
      _id,
      color: color,
      type: this.type,
      name: name.trim(),
    };
    this.isCreateMode = false;

    this.httpService.updateTag(tag).then(
      (data) => {
        for (let i = 0; i < this.tagsResult.length; i++) {
          if (this.tagsResult[i]._id === _id) {
            const tag = this.helperService.deepCopy(this.tagsResult[i]);
            this.tagsResult[i].name = name;
            this.tagsResult[i].color = color;
            this.onSelect(this.tagsResult[i]);
            this.appUpdateTag.emit({
              previous: tag,
              current: this.tagsResult[i],
            });
            break;
          }
        }

        this.appUpdateTag.emit(data);
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  onDelete(tag: any) {
    this.selectedTags = differenceBy(this.selectedTags, [tag], this.tagLabelField);
    this.appDelete.emit(this.selectedTags);
  }

  handleTagClick(tag: any) {
    this.appClick.emit(tag);
  }

  /** hide auto complete */
  onClickOutside() {
    this.isCreateMode = false;
  }

  deleteTag(tag: any) {
    const ref = this.diaogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Delete tag '${tag.name}'`,
        message: `Are you sure you want to delete tag '${tag.name}'?`,
        button: {
          success: { title: 'Confirm' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess }) => {
      if (isSuccess) {
        if (tag._id) {
          this.httpService.deleteTag(tag._id).then(
            () => {
              // this.onDelete(tag);

              if (this._tags.length) {
                for (let i = 0; i < this._tags.length; i++) {
                  if (this._tags[i]._id === tag._id) {
                    this._tags.splice(i, 1);
                    this.appUpdateTagList.emit(this._tags);
                    break;
                  }
                }

                this.tagsResult = this.tagsResult.filter(({ _id }) => _id !== tag._id);
              }
              this.appTagDeleted.emit(tag);
              this.helperService.showToaster('success', 'Successfully deleted');
            },
            (err) => {
              this.helperService.showToaster('error', err);
            }
          );
        } else {
          if (this._tags.length) {
            for (let i = 0; i < this._tags.length; i++) {
              if (this._tags[i].name === tag.name && this._tags[i].color === tag.color) {
                this._tags.splice(i, 1);
                this.appUpdateTagList.emit(this._tags);
                break;
              }
            }
            for (let i = 0; i < this.selectedTags.length; i++) {
              if (this.selectedTags[i].name === tag.name && this.selectedTags[i].color === tag.color) {
                this.selectedTags.splice(i, 1);
                break;
              }
            }
            this.tagsResult = this.tagsResult.filter(({ name, color }) => (name !== tag.name) && (color !== tag.color));
            this.appTagDeleted.emit(tag);
            this.helperService.showToaster('success', 'Successfully deleted');
          }
        }
      }
      onClose.unsubscribe();
    });
  }
}
